import { useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import ZipProjectService from "@app/projects/zipProjectService";
import { UserContext } from "@app/user/UserContext";

export interface ZipCoverageZipMembership {
  /** Zip codes the analysis recorded members for on this run configuration. */
  zipCodesWithMembership: Set<string>;
  /**
   * False until the fetch answers (and if it fails). An empty set then means "nothing has members" rather than
   * "not asked yet" — the difference between hiding nothing and hiding the entire Service Area list.
   */
  hasMembershipData: boolean;
  isLoading: boolean;
}

const EMPTY: Set<string> = new Set();

/**
 * Loads the zip codes that hold membership on a run configuration, so the Service Area drill-down can tell which of
 * its zips have anything to draw. The tree itself comes from geography (`useZipCoverageServiceAreas`), which knows
 * nothing about members.
 *
 * Keyed on the run configuration the map will actually use — the drafted Results As Of resolved through the
 * Primary/Secondary membership type — so the classification can't come from a different timeslice than the map.
 * A zip code absent from the response has either zero membership everywhere or no analysis row at all; both mean the
 * same thing to the selection list.
 */
export const useZipCoverageZipMembership = (
  projectId: number | null,
  runConfigurationId: number | null,
): ZipCoverageZipMembership => {
  const zipProjectService = useMemo(() => new ZipProjectService(), []);
  const user = useContext(UserContext);

  const { data, isFetching } = useQuery({
    enabled: projectId !== null && runConfigurationId !== null,
    queryFn: async ({ signal }) => {
      const zipCodes = await zipProjectService.getZipCodesWithMembership(projectId!, runConfigurationId!, signal);
      return new Set(zipCodes ?? []);
    },
    queryKey: ["zip-coverage-map", "zip-membership", user?.userId, projectId, runConfigurationId],
    staleTime: 5 * 60 * 1000,
  });

  return { hasMembershipData: data !== undefined, isLoading: isFetching, zipCodesWithMembership: data ?? EMPTY };
};
